import { useCallback, useMemo } from 'react';
import type { FunctionComponent } from 'react';

import { useLocation, useParams } from '../../../contexts/RouterContext';
import { useCurrentProject } from '../../../hooks/api/projects/useCurrentProject';
import { getProjectQueryKey } from '../../../hooks/api/projects/useGetProject';
import queryClient from '../../../ReactQuery/client';
import { nuc } from '../../../utils';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import ProjectSettingsContextProvider from './context';
import {
  useCanUserEditProject,
  useCanUserSetSBOMPolicy,
  useCanUserSetSomeIssueFilter,
  useCanUserSetSomeIssuePolicy,
} from './permissions';

type ProjectSettingsPath = 'settings' | 'issues' | 'issue-tracker' | 'notifications' | 'update-hooks' | 'builds' | 'policies';

/**
 * ProjectSettingsContainer wraps all the settings components. It consists of the
 * sidebar navigation and the content area. It also provides the context for the
 * settings pages to consume.
 */
const ProjectSettingsContainer: FunctionComponent = ({
  children,
}) => {
  const params = useParams<RevisionRouteParams>();
  const location = useLocation();
  const { data: project } = useCurrentProject();

  const baseProjectURL = useMemo(() => `/projects/${nuc(params.project_id)}/settings`, [params.project_id]);
  const isPathActive = useCallback((pathPart: ProjectSettingsPath) => {
    const pathWithoutTrailingSlash = location.pathname.replace(/\/$/, '');
    return pathWithoutTrailingSlash.endsWith(pathPart);
  }, [location.pathname]);

  const locator = project?.locator ?? '';
  const onReloadProject = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getProjectQueryKey(locator) });
  }, [locator]);

  const canEdit = useCanUserEditProject(locator);
  const canSetSomeIssuePolicy = useCanUserSetSomeIssuePolicy(locator);
  const canSetSBOMPolicy = useCanUserSetSBOMPolicy(locator);
  const canSetSomeIssueFilter = useCanUserSetSomeIssueFilter(locator);
  const shouldShowPolicies = canEdit || canSetSomeIssuePolicy || canSetSBOMPolicy || canSetSomeIssueFilter;

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavHeading>
          Settings
        </SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem
          to={baseProjectURL}
          isActive={isPathActive('settings')}
        >
          General
        </SidebarNavLayout.NavItem>
        {shouldShowPolicies && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/policies`}
            isActive={isPathActive('policies')}
          >
            Policies
          </SidebarNavLayout.NavItem>
        )}
        {/*
          Note that we are intentionally not using a Fragment here,
          because the SidebarNavLayout (and other components using the slots pattern)
          can only look at the direct children of the Nav component, meaning that
          if they are wrapped in a Fragment, they will not be rendered.
        */}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/issue-tracker`}
            isActive={isPathActive('issue-tracker')}
          >
            Issue Tracker
          </SidebarNavLayout.NavItem>
        )}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/notifications`}
            isActive={isPathActive('notifications')}
          >
            Notifications
          </SidebarNavLayout.NavItem>
        )}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/update-hooks`}
            isActive={isPathActive('update-hooks')}
          >
            Hooks
          </SidebarNavLayout.NavItem>
        )}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/builds`}
            isActive={isPathActive('builds')}
          >
            Builds
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        {project && (
          <ProjectSettingsContextProvider
            project={project}
            onReloadProject={onReloadProject}
          >
            {children}
          </ProjectSettingsContextProvider>
        )}
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

export default ProjectSettingsContainer;
